/**
 * Copyright (c) Aleksei Volkov and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useHistory, useLocation } from 'react-router'
import * as PropTypes from 'prop-types'
import useAuthStore from 'context/AuthStore/useAuthStore'
import { HOME_PATH, SIGN_IN_PATH } from 'routes/constants.routes'
import { LAST_VISIT, PREV_PAGE, SessionStorageService } from 'lib/utils'

/**
 * Returns functions work with locations.
 *  - goTo("/home")
 *  @description Pushing to next page and save Prev page
 *   - push("/home")
 *  @description Pushing to next page
 *  - setLastVisit("/home")
 *  @description Setting last page.
 *  - setPrevPage("/home")
 *  @description Setting previous page.
 *  - goPrevPage()
 *  @description Return to previous page.
 *  - prevPage
 *  @description Return previous page
 *  - lastVisit
 *  @description Return previous page
 * - isReloadedPage()
 *  @description Return true or false
 *  - unAuthByStatus(401)
 *  @description Return to signIn if status(401).
 *
 */

const useRoutes = () => {
  const { authStoreFunc } = useAuthStore()
  const history = useHistory()
  const location = useLocation()

  /**{(string|null)}
   * Return last visit page "/url"
   * or Null.
   */
  const lastVisit = SessionStorageService.getLastVisitedPage()

  /**{(string|null)}
   * Return previous page "/url"
   * or Null.
   */
  const prevPage = SessionStorageService.getPrevPage()

  /**
   * @function setLastVisit
   * Setting last page.
   *
   * @param {string} path is the url.
   * @example setLastVisit("/home")
   */
  const setLastVisit = path => {
    SessionStorageService.setLastVisit(checkOnUndef(path))
  }
  setLastVisit.propTypes = {
    path: PropTypes.string.isRequired
  }

  const checkOnUndef = path => (path?.includes('undefined') ? HOME_PATH : path)

  /**
   * @function setPrevPage
   * Setting previous page.
   *
   * @param {string} path is the url .
   * @example setPrevPage("/home")
   */
  const setPrevPage = path => {
    SessionStorageService.setPrevPage(checkOnUndef(path))
  }
  setPrevPage.propTypes = {
    path: PropTypes.string.isRequired
  }
  /**@function goTo
   * Pushing to next page
   * and save previous page.
   * @param {string} path is the url.
   * @example goTo("/home")
   */
  const goTo = path => {
    setPrevPage(location.pathname)
    setLastVisit(path)
    history.push(path)
  }

  goTo.propTypes = {
    path: PropTypes.string.isRequired
  }

  /**@function push
   * Pushing to next page
   * @param {string} path is the url.
   * @example push("/home")
   */
  const push = path => {
    setPrevPage(path)
    setLastVisit(path)
    history.push(path)
  }

  push.propTypes = {
    path: PropTypes.string.isRequired
  }
  /**
   * @function goPrevPage
   * Return to previous page.
   *  @example goPrevPage()
   */
  const goPrevPage = () => {
    const last = sessionStorage.getItem(LAST_VISIT)
    const prev = sessionStorage.getItem(PREV_PAGE)
    const path =
      last && prev && prev !== SIGN_IN_PATH
        ? last.toString() === prev.toString()
          ? HOME_PATH
          : prev.toString()
        : HOME_PATH
    goTo(path)
  }

  /**
   * @function unAuthByStatus
   * @param {(string|number)} status 401
   * Return to signIn.
   *  @example unAuthByStatus(401)
   */
  const unAuthByStatus = status => {
    if (status && (Number(status) === 401 || Number(status) === 403)) {
      authStoreFunc?.logout && authStoreFunc.logout()
      return goTo(SIGN_IN_PATH)
    }
    return true
  }
  /**
   * @function isReloadedPage
   * Return  isReloadedPage ?.
   *  @example isReloadedPage()
   */
  const isReloadedPage = () => {
    let prev = sessionStorage.getItem(PREV_PAGE)
    return location.pathname === prev.toString()
  }
  return {
    goTo,
    setLastVisit,
    setPrevPage,
    goPrevPage,
    push,
    unAuthByStatus,
    isReloadedPage,
    prevPage,
    lastVisit
  }
}
export default useRoutes
